/**
 * Vosk Speech Recognition Service
 *
 * Provides offline, Wasm-based speech recognition that works with
 * Web Audio API streams. Solves Android mic exclusivity issue where
 * Web Speech API cannot coexist with MediaRecorder.
 *
 * Uses vosk-browser package which bundles the Vosk Wasm runtime.
 * Model is downloaded on first use and cached by the browser.
 */

import { createModel } from 'vosk-browser';

// German small model (~50MB) - hosted by Alphacephei
// Alternative smaller models available but with lower accuracy
const MODEL_URL = 'https://ccoreilly.github.io/vosk-browser/models/vosk-model-small-de-0.15.tar.gz';

// Module-level state
let model = null;
let isModelLoading = false;
let modelLoadPromise = null;
let loadError = null;

/**
 * Load Vosk model (lazy, cached)
 * Model is downloaded once and cached by the browser's HTTP cache.
 *
 * @param {function} onProgress - Progress callback receiving { loaded, total, percent }
 * @returns {Promise<void>}
 */
export const loadModel = async (onProgress) => {
  // Already loaded
  if (model) {
    onProgress?.({ loaded: 100, total: 100, percent: 100 });
    return model;
  }

  // Already loading - wait for existing promise
  if (isModelLoading && modelLoadPromise) {
    return modelLoadPromise;
  }

  // Previous load failed - allow retry
  if (loadError) {
    loadError = null;
  }

  isModelLoading = true;

  modelLoadPromise = (async () => {
    try {
      console.log('[VoskService] Loading model from:', MODEL_URL);

      model = await createModel(MODEL_URL, (progress) => {
        // Progress is 0-1
        const percent = Math.round(progress * 100);
        console.log(`[VoskService] Model load progress: ${percent}%`);
        onProgress?.({
          loaded: percent,
          total: 100,
          percent
        });
      });

      console.log('[VoskService] Model loaded successfully');
      isModelLoading = false;
      return model;

    } catch (err) {
      console.error('[VoskService] Failed to load model:', err);
      loadError = err;
      isModelLoading = false;
      modelLoadPromise = null;
      throw err;
    }
  })();

  return modelLoadPromise;
};

/**
 * Check if model is loaded and ready
 * @returns {boolean}
 */
export const isModelLoaded = () => model !== null;

/**
 * Check if model is currently loading
 * @returns {boolean}
 */
export const isLoading = () => isModelLoading;

/**
 * Get any load error that occurred
 * @returns {Error|null}
 */
export const getLoadError = () => loadError;

/**
 * Create a new recognizer instance for a given sample rate
 *
 * @param {number} sampleRate - Audio sample rate (e.g., 16000, 44100, 48000)
 * @returns {KaldiRecognizer} Recognizer instance
 * @throws {Error} If model not loaded
 */
export const createRecognizer = (sampleRate = 16000) => {
  if (!model) {
    throw new Error('[VoskService] Model not loaded. Call loadModel() first.');
  }

  console.log(`[VoskService] Creating recognizer with sample rate: ${sampleRate}`);
  const recognizer = new model.KaldiRecognizer(sampleRate);
  return recognizer;
};

/**
 * Get model information
 * @returns {Object} Model metadata
 */
export const getModelInfo = () => ({
  url: MODEL_URL,
  language: 'German (de-DE)',
  type: 'small',
  estimatedSize: '~50MB',
  description: 'Vosk small German model for keyword detection'
});

/**
 * Cleanup - note: we keep the model loaded for reuse
 * Only call this if you need to free memory and won't use Vosk again
 */
export const unloadModel = () => {
  if (model) {
    // vosk-browser doesn't have explicit cleanup, but we can clear the reference
    model = null;
    modelLoadPromise = null;
    console.log('[VoskService] Model unloaded');
  }
};
